import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";

// ---------------------------------------------------------------------------
// Small reusable helpers
// ---------------------------------------------------------------------------
function Field({ label, children, help, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</Label>
      {children}
      {help && <div className="text-[11px] text-neutral-500">{help}</div>}
    </div>
  );
}

function useLookups(category) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!category) return;
    setBusy(true);
    try {
      const { data } = await api.get("/lookups", { params: { category, page: 1, page_size: 200 } });
      setItems(data.items || []);
    } finally { setBusy(false); }
  }, [category]);
  useEffect(() => { load(); }, [load]);
  return { items, busy, reload: load };
}

function useCollection(path, params = {}) {
  const [items, setItems] = useState([]);
  const stableParams = JSON.stringify(params);
  useEffect(() => {
    let mounted = true;
    api.get(path, { params: { page: 1, page_size: 200, ...JSON.parse(stableParams) } })
      .then(({ data }) => { if (mounted) setItems(data.items || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [path, stableParams]);
  return items;
}

// Inline "add lookup" mini-form used by the Product form so admins don't
// need to leave the flow when a new category/variant/pack_size is missing.
function InlineAddLookup({ category, onAdded }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!code.trim() || !label.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/lookups", {
        category, code: code.trim().toUpperCase(), label: label.trim(),
      });
      toast.success(`Added ${category} → ${data.code}`);
      setCode(""); setLabel(""); setOpen(false);
      onAdded?.(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to add lookup");
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
              className="text-[11px] text-[#E5197F] hover:underline"
              data-testid={`inline-add-${category}`}>
        + Add {category.replace(/_/g, " ")}
      </button>
    );
  }
  return (
    <div className="mt-2 p-2 border border-dashed border-[#E5197F]/40 rounded-lg space-y-2 bg-[#E5197F]/5">
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="CODE (e.g. GREEN)" value={code} onChange={e=>setCode(e.target.value)}
               data-testid={`inline-add-${category}-code`} className="h-8 text-xs"/>
        <Input placeholder="Label" value={label} onChange={e=>setLabel(e.target.value)}
               data-testid={`inline-add-${category}-label`} className="h-8 text-xs"/>
      </div>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={()=>setOpen(false)} className="h-7 text-xs">Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy}
                className="h-7 text-xs tulsi-gradient text-white"
                data-testid={`inline-add-${category}-save`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product form
// ---------------------------------------------------------------------------
export function ProductForm({ open, onOpenChange, onSaved, existing = null }) {
  const isEdit = !!existing;
  const cats = useLookups("product_category");
  const variants = useLookups("product_variant");
  const packs = useLookups("product_pack_size");
  const blank = {
    code: "", name: "", brand: "Tulsi", hsn: "", description: "",
    category_lookup_id: "", variant_lookup_id: "", pack_size_lookup_id: "",
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setForm(blank); return; }
    if (existing) {
      setForm({
        code: existing.code || "", name: existing.name || "",
        brand: existing.brand || "", hsn: existing.hsn || "",
        description: existing.description || "",
        category_lookup_id: existing.category_lookup_id || "",
        variant_lookup_id: existing.variant_lookup_id || "",
        pack_size_lookup_id: existing.pack_size_lookup_id || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const patch = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        // SKUs link by product_id, so renaming the code is safe; the backend
        // upper-cases it and rejects a code already used by another product.
        const payload = { ...form, code: form.code.trim() };
        if (!payload.code) delete payload.code;
        const { data } = await api.patch(`/products/${existing.id}`, payload);
        toast.success(`Product ${data.code} updated`);
        onSaved?.(data);
      } else {
        const payload = { ...form };
        if (!payload.code) delete payload.code;
        const { data } = await api.post("/products", payload);
        toast.success(`Product ${data.code} created`);
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription>Category → Variant → Pack size drives auto-code.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code" help="Leave blank to auto-generate">
            <Input value={form.code} onChange={e=>patch("code", e.target.value)} placeholder="AUTO"
                   data-testid="product-form-code"/>
          </Field>
          <Field label="Name">
            <Input value={form.name} onChange={e=>patch("name", e.target.value)}
                   data-testid="product-form-name"/>
          </Field>
          <Field label="Category">
            <Select value={form.category_lookup_id} onValueChange={v=>patch("category_lookup_id", v)}>
              <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Select category…"/></SelectTrigger>
              <SelectContent>
                {cats.items.map(l => <SelectItem key={l.id} value={l.id}>{l.label} ({l.code})</SelectItem>)}
              </SelectContent>
            </Select>
            <InlineAddLookup category="product_category" onAdded={()=>cats.reload()}/>
          </Field>
          <Field label="Variant">
            <Select value={form.variant_lookup_id} onValueChange={v=>patch("variant_lookup_id", v)}>
              <SelectTrigger data-testid="product-form-variant"><SelectValue placeholder="Select variant…"/></SelectTrigger>
              <SelectContent>
                {variants.items.map(l => <SelectItem key={l.id} value={l.id}>{l.label} ({l.code})</SelectItem>)}
              </SelectContent>
            </Select>
            <InlineAddLookup category="product_variant" onAdded={()=>variants.reload()}/>
          </Field>
          <Field label="Pack size">
            <Select value={form.pack_size_lookup_id} onValueChange={v=>patch("pack_size_lookup_id", v)}>
              <SelectTrigger data-testid="product-form-pack"><SelectValue placeholder="Select pack size…"/></SelectTrigger>
              <SelectContent>
                {packs.items.map(l => <SelectItem key={l.id} value={l.id}>{l.label} ({l.code})</SelectItem>)}
              </SelectContent>
            </Select>
            <InlineAddLookup category="product_pack_size" onAdded={()=>packs.reload()}/>
          </Field>
          <Field label="Brand">
            <Input value={form.brand} onChange={e=>patch("brand", e.target.value)}
                   data-testid="product-form-brand"/>
          </Field>
          <Field label="HSN">
            <Input value={form.hsn} onChange={e=>patch("hsn", e.target.value)}
                   data-testid="product-form-hsn"/>
          </Field>
          <div className="col-span-2">
            <Field label="Description">
              <Textarea value={form.description} onChange={e=>patch("description", e.target.value)}
                        data-testid="product-form-desc"/>
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name || !form.category_lookup_id ||
                                              !form.variant_lookup_id || !form.pack_size_lookup_id}
                  className="tulsi-gradient text-white font-semibold"
                  data-testid="product-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : null}
            {isEdit ? "Save changes" : "Create product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SKU form (create + edit)
// ---------------------------------------------------------------------------
const round2 = (n) => Math.round(n * 100) / 100;
const round3 = (n) => Math.round(n * 1000) / 1000;

export function SkuForm({ open, onOpenChange, onSaved, existing = null }) {
  const products = useCollection("/products", { is_active: true });
  const uomLookups = useLookups("uom");
  const empty = {
    sku_code: "", product_id: "", mrp: 0, gst_rate: 5,
    uom: "PCS", pieces_per_case: "", weight_kg: "", barcode: "",
    // Phase 26D UOM engine — which units the field/secondary-order screens
    // let an SE pick when entering a line. PCS is always implicit even if
    // not explicitly listed. KG needs weight_kg filled; BAG needs
    // bag_conversion_factor. (Hotfix — form collects kg directly now, same
    // unit the field app and reports actually use; backend still stores
    // weight_g alongside for older readers, converted on submit below.)
    allowed_uoms: ["PCS"], bag_conversion_factor: "", bunch_conversion_factor: "",
    // Per-bag convenience inputs — kg_per_bag / bunch_per_bag are never sent
    // to the backend directly. They're just an alternate way to fill in
    // weight_kg / bunch_conversion_factor for admins who know "1 bag = X kg"
    // or "1 bag = X bunches" (how the warehouse actually counts stock) but
    // not the per-piece figure, given pieces-per-bag is already on hand.
    kg_per_bag: "", bunch_per_bag: "",
    // A pure scheme-giveaway article (e.g. a free-goods "Get SKU" that's
    // never sold on its own) still needs a real SKU record, but shouldn't
    // show up in the normal order-entry SKU picker. Default true — this
    // only needs unchecking for a handful of promotional items.
    is_orderable: true,
  };
  const [form, setForm] = useState(empty);
  // Price-per-unit trio — Pcs is the canonical value the backend stores
  // (base_price). Kg/Bag are just that same price expressed per-kg / per-bag
  // via Weight (kg) / Pieces-per-bag, so an admin can enter whichever unit
  // they actually think in without doing the conversion math by hand (that
  // manual math is exactly how base_price ended up wrong — as a per-kg
  // figure stored where a per-piece figure belonged — for 37 SKUs before).
  const [prices, setPrices] = useState({ pcs: "", kg: "", bag: "" });
  const [busy, setBusy] = useState(false);
  const isEdit = !!existing?.id;

  const weightKg = Number(form.weight_kg) || 0;
  const bagFactor = Number(form.bag_conversion_factor) || 0;

  useEffect(() => {
    if (!open) { setForm(empty); setPrices({ pcs: "", kg: "", bag: "" }); return; }
    if (existing) {
      const wKg = existing.weight_per_pc_kg ?? (existing.weight_g ? existing.weight_g / 1000 : 0);
      const bFactor = existing.bag_conversion_factor ?? 0;
      const bunchFactor = existing.bunch_conversion_factor ?? 0;
      const pcsPrice = existing.base_price ?? 0;
      setForm({
        sku_code: existing.sku_code || "",
        product_id: existing.product_id || "",
        mrp: existing.mrp ?? 0,
        gst_rate: existing.gst_rate ?? 5,
        uom: existing.uom || "PCS",
        pieces_per_case: existing.pieces_per_case ?? "",
        weight_kg: wKg || "",
        barcode: existing.barcode || "",
        allowed_uoms: (existing.allowed_uoms && existing.allowed_uoms.length)
          ? existing.allowed_uoms : ["PCS"],
        bag_conversion_factor: bFactor || "",
        bunch_conversion_factor: bunchFactor || "",
        kg_per_bag: wKg > 0 && bFactor > 0 ? String(round3(wKg * bFactor)) : "",
        bunch_per_bag: bFactor > 0 && bunchFactor > 0 ? String(round3(bFactor / bunchFactor)) : "",
        is_orderable: existing.is_orderable !== false,
      });
      setPrices({
        pcs: pcsPrice ? String(pcsPrice) : "",
        // Prefer the stored canonical price_per_kg over recomputing from
        // base_price, so a previously-saved rate displays exactly as saved.
        kg: existing.price_per_kg
          ? String(existing.price_per_kg)
          : (pcsPrice && wKg > 0 ? String(round2(pcsPrice / wKg)) : ""),
        bag: pcsPrice && bFactor > 0 ? String(round2(pcsPrice * bFactor)) : "",
      });
    } else {
      setForm(empty);
      setPrices({ pcs: "", kg: "", bag: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  // Weight/bag-factor changed after a price was already entered — keep the
  // per-kg / per-bag displays in sync with the canonical per-piece price.
  useEffect(() => {
    setPrices((p) => {
      const pcsNum = p.pcs !== "" ? Number(p.pcs) : NaN;
      if (isNaN(pcsNum)) return p;
      return {
        ...p,
        kg: weightKg > 0 ? String(round2(pcsNum / weightKg)) : "",
        bag: bagFactor > 0 ? String(round2(pcsNum * bagFactor)) : "",
      };
    });
  }, [weightKg, bagFactor]);

  // "Pieces per bag" changed (or just got set) — keep Kg-per-bag/Weight(kg)
  // and Bunches-per-bag/Pieces-per-bunch in sync with each other, preferring
  // whichever per-piece figure is already on hand.
  useEffect(() => {
    setForm((f) => {
      if (bagFactor <= 0) return f;
      const next = { ...f };
      const wKg = f.weight_kg !== "" ? Number(f.weight_kg) : NaN;
      const kgPerBag = f.kg_per_bag !== "" ? Number(f.kg_per_bag) : NaN;
      if (!isNaN(wKg)) next.kg_per_bag = String(round3(wKg * bagFactor));
      else if (!isNaN(kgPerBag)) next.weight_kg = String(round3(kgPerBag / bagFactor));
      const pcsPerBunch = f.bunch_conversion_factor !== "" ? Number(f.bunch_conversion_factor) : NaN;
      const bunchPerBag = f.bunch_per_bag !== "" ? Number(f.bunch_per_bag) : NaN;
      if (!isNaN(pcsPerBunch) && pcsPerBunch > 0) next.bunch_per_bag = String(round3(bagFactor / pcsPerBunch));
      else if (!isNaN(bunchPerBag) && bunchPerBag > 0) next.bunch_conversion_factor = String(round3(bagFactor / bunchPerBag));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bagFactor]);

  const setWeightFromPc = (val) => {
    const num = val !== "" ? Number(val) : NaN;
    setForm((f) => ({
      ...f,
      weight_kg: val,
      kg_per_bag: !isNaN(num) && bagFactor > 0 ? String(round3(num * bagFactor)) : f.kg_per_bag,
    }));
  };
  const setWeightFromBag = (val) => {
    const num = val !== "" ? Number(val) : NaN;
    setForm((f) => ({
      ...f,
      kg_per_bag: val,
      weight_kg: !isNaN(num) && bagFactor > 0 ? String(round3(num / bagFactor)) : f.weight_kg,
    }));
  };
  const setBunchFactorFromPieces = (val) => {
    const num = val !== "" ? Number(val) : NaN;
    setForm((f) => ({
      ...f,
      bunch_conversion_factor: val,
      bunch_per_bag: !isNaN(num) && num > 0 && bagFactor > 0 ? String(round3(bagFactor / num)) : f.bunch_per_bag,
    }));
  };
  const setBunchFromBag = (val) => {
    const num = val !== "" ? Number(val) : NaN;
    setForm((f) => ({
      ...f,
      bunch_per_bag: val,
      bunch_conversion_factor: !isNaN(num) && num > 0 && bagFactor > 0 ? String(round3(bagFactor / num)) : f.bunch_conversion_factor,
    }));
  };

  const setPriceFromPcs = (val) => {
    const num = val !== "" ? Number(val) : NaN;
    setPrices({
      pcs: val,
      kg: !isNaN(num) && weightKg > 0 ? String(round2(num / weightKg)) : "",
      bag: !isNaN(num) && bagFactor > 0 ? String(round2(num * bagFactor)) : "",
    });
  };
  const setPriceFromKg = (val) => {
    if (weightKg <= 0) { setPrices((p) => ({ ...p, kg: val })); return; }
    const num = val !== "" ? Number(val) : NaN;
    const pcsNum = !isNaN(num) ? round2(num * weightKg) : null;
    setPrices({
      pcs: pcsNum == null ? "" : String(pcsNum),
      kg: val,
      bag: pcsNum != null && bagFactor > 0 ? String(round2(pcsNum * bagFactor)) : "",
    });
  };
  const setPriceFromBag = (val) => {
    if (bagFactor <= 0) { setPrices((p) => ({ ...p, bag: val })); return; }
    const num = val !== "" ? Number(val) : NaN;
    const pcsNum = !isNaN(num) ? round2(num / bagFactor) : null;
    setPrices({
      pcs: pcsNum == null ? "" : String(pcsNum),
      kg: pcsNum != null && weightKg > 0 ? String(round2(pcsNum / weightKg)) : "",
      bag: val,
    });
  };

  const patch = (k, v) => setForm(s => ({ ...s, [k]: v }));

  // Client-side sanity so the user gets a specific message before the network
  // round-trip. Backend also enforces these.
  const clientValidate = () => {
    if (!form.sku_code) return "SKU code is required";
    if (!form.product_id) return "Select a product";
    if (Number(form.mrp) <= 0) return "MRP must be greater than 0";
    if (Number(prices.pcs) <= 0) return "Price per Pcs must be greater than 0";
    if (Number(prices.pcs) > Number(form.mrp))
      return `Price per Pcs ₹${prices.pcs} cannot exceed MRP ₹${form.mrp}`;
    if (!["0", "5", "12", "18", "28", 0, 5, 12, 18, 28].includes(form.gst_rate) &&
        ![0, 5, 12, 18, 28].includes(Number(form.gst_rate)))
      return "GST rate must be one of 0, 5, 12, 18, 28";
    // Mirror backend validate_sku_uom_fields (routes_uoms.py) so the error
    // surfaces before the round-trip.
    if (form.allowed_uoms.includes("KG") && !Number(form.weight_kg))
      return "Enabling KG requires Weight (kg) to be set";
    if (form.allowed_uoms.includes("BAG") && !Number(form.bag_conversion_factor))
      return "Enabling BAG requires Pieces per bag to be set";
    if (form.allowed_uoms.includes("BUNCH") && !Number(form.bunch_conversion_factor))
      return "Enabling BUNCH requires Pieces per bunch to be set";
    return null;
  };

  const toggleUom = (u) => setForm((s) => ({
    ...s,
    allowed_uoms: s.allowed_uoms.includes(u)
      ? s.allowed_uoms.filter((x) => x !== u)
      : [...s.allowed_uoms, u],
  }));

  const submit = async () => {
    const clientErr = clientValidate();
    if (clientErr) { toast.error(clientErr); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        mrp: Number(form.mrp), base_price: Number(prices.pcs), gst_rate: Number(form.gst_rate),
        // Kg-canonical — send the rate the admin actually typed/derived so
        // the backend doesn't have to re-derive it from base_price alone.
        price_per_kg: prices.kg === "" || prices.kg == null ? null : Number(prices.kg),
        pieces_per_case: form.pieces_per_case === "" || form.pieces_per_case == null
          ? null : Number(form.pieces_per_case),
        // Backend still stores weight_g (grams) for older readers; the form
        // collects kg directly (the unit the field app and reports actually
        // use), converted here rather than asking the user to do the math.
        weight_g: form.weight_kg === "" || form.weight_kg == null
          ? null : Number(form.weight_kg) * 1000,
        allowed_uoms: form.allowed_uoms,
        bag_conversion_factor: form.bag_conversion_factor === "" || form.bag_conversion_factor == null
          ? null : Number(form.bag_conversion_factor),
        bunch_conversion_factor: form.bunch_conversion_factor === "" || form.bunch_conversion_factor == null
          ? null : Number(form.bunch_conversion_factor),
      };
      if (isEdit) {
        // Orders/stock link by SKU id, so code and product can both be corrected.
        // Only send them when changed (backend re-checks code uniqueness).
        const { sku_code, product_id, ...rest } = payload;
        if (sku_code && sku_code !== existing.sku_code) rest.sku_code = sku_code;
        if (product_id && product_id !== existing.product_id) rest.product_id = product_id;
        const { data } = await api.patch(`/skus/${existing.id}`, rest);
        toast.success(`SKU ${data.sku_code} updated`);
      } else {
        const { data } = await api.post("/skus", payload);
        toast.success(`SKU ${data.sku_code} created`);
      }
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const productOptions = products.map(p => ({ id: p.id, code: p.code, name: p.name }));
  const uomOptions = [
    { id: "PCS", code: "PCS", name: "Pieces" },
    { id: "CASE", code: "CASE", name: "Case" },
    { id: "KG", code: "KG", name: "Kilogram" },
    { id: "G", code: "G", name: "Gram" },
    ...uomLookups.items.map(l => ({ id: l.code, code: l.code, name: l.label })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sku-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit SKU ${existing?.sku_code || ""}` : "New SKU"}</DialogTitle>
          <DialogDescription>Stock-keeping unit tied to a product.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="SKU code">
            <Input value={form.sku_code}
                   onChange={e=>patch("sku_code", e.target.value.toUpperCase())}
                   data-testid="sku-form-code"/>
          </Field>
          <Field label="Product">
            <SearchableSelect
              value={form.product_id}
              onChange={(v) => patch("product_id", v)}
              options={productOptions}
              placeholder="Search product by code or name…"
              testid="sku-form-product"
            />
          </Field>
          <Field label="MRP (₹)"><Input type="number" value={form.mrp} onChange={e=>patch("mrp", e.target.value)} data-testid="sku-form-mrp"/></Field>
          <Field label="Weight (kg)" help="Weight of ONE piece, in kg — e.g. a 250g pack = 0.25. Set this first so Price per Kg can be entered/derived.">
            <Input type="number" step="0.001" value={form.weight_kg ?? ""} onChange={e=>setWeightFromPc(e.target.value)} data-testid="sku-form-weight"/>
          </Field>
          <div className="col-span-2 grid grid-cols-3 gap-4 rounded-xl border border-neutral-200 p-3">
            <Field label="Price per Kg (₹)"
                   help={weightKg > 0 ? "Canonical — the rate you actually think in. Everything else derives from this." : "Set Weight (kg) above first."}>
              <Input type="number" value={prices.kg} disabled={weightKg <= 0}
                     onChange={e=>setPriceFromKg(e.target.value)} data-testid="sku-form-price-kg"/>
            </Field>
            <Field label="Price per Pcs (₹)"
                   help="Auto-synced with Price per Kg — this is what actually gets billed per piece.">
              <Input type="number" value={prices.pcs} onChange={e=>setPriceFromPcs(e.target.value)} data-testid="sku-form-price-pcs"/>
            </Field>
            <Field label="Price per Bag (₹)"
                   help={bagFactor > 0 ? "Auto-synced with Price per Kg." : "Enable BAG + set Pieces per bag first."}>
              <Input type="number" value={prices.bag} disabled={bagFactor <= 0}
                     onChange={e=>setPriceFromBag(e.target.value)} data-testid="sku-form-price-bag"/>
            </Field>
          </div>
          <Field label="GST %"><Input type="number" value={form.gst_rate} onChange={e=>patch("gst_rate", e.target.value)} data-testid="sku-form-gst"/></Field>
          <Field label="UOM">
            <SearchableSelect
              value={form.uom} onChange={(v) => patch("uom", v)}
              options={uomOptions} placeholder="Select unit of measure…"
              clearable={false}
              testid="sku-form-uom"
            />
          </Field>
          <Field label="Pieces per case"><Input type="number" value={form.pieces_per_case ?? ""} onChange={e=>patch("pieces_per_case", e.target.value)} data-testid="sku-form-ppc"/></Field>
          <div className="col-span-2">
            <Field label="Order units (SE app / secondary order)"
                   help="Primary/secondary order entry only offers Kg, Bag, or Bunch — enable whichever units this SKU can be ordered in.">
              <div className="flex gap-2">
                {["PCS", "KG", "BAG", "BUNCH"].map((u) => (
                  <button key={u} type="button"
                          onClick={() => u !== "PCS" && toggleUom(u)}
                          disabled={u === "PCS"}
                          data-testid={`sku-form-uom-toggle-${u.toLowerCase()}`}
                          className={"px-3 h-9 rounded-lg text-xs font-semibold border "
                            + (form.allowed_uoms.includes(u)
                                ? "bg-[#6D2C91] text-white border-[#6D2C91]"
                                : "bg-white text-neutral-600 border-neutral-200")
                            + (u === "PCS" ? " opacity-70 cursor-default" : "")}>
                    {u}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          {form.allowed_uoms.includes("BAG") && (
            <Field label="Pieces per bag">
              <Input type="number" value={form.bag_conversion_factor ?? ""}
                     onChange={e=>patch("bag_conversion_factor", e.target.value)}
                     data-testid="sku-form-bag-factor"/>
            </Field>
          )}
          {form.allowed_uoms.includes("BAG") && (
            <Field label="Kg per bag"
                   help={bagFactor > 0 ? "Auto-synced with Weight (kg) above." : "Set Pieces per bag first."}>
              <Input type="number" step="0.001" value={form.kg_per_bag ?? ""} disabled={bagFactor <= 0}
                     onChange={e=>setWeightFromBag(e.target.value)} data-testid="sku-form-kg-per-bag"/>
            </Field>
          )}
          {form.allowed_uoms.includes("BUNCH") && (
            <Field label="Pieces per bunch">
              <Input type="number" value={form.bunch_conversion_factor ?? ""}
                     onChange={e=>setBunchFactorFromPieces(e.target.value)}
                     data-testid="sku-form-bunch-factor"/>
            </Field>
          )}
          {form.allowed_uoms.includes("BUNCH") && (
            <Field label="Bunches per bag"
                   help={bagFactor > 0 ? "Auto-synced with Pieces per bunch via Pieces per bag." : "Set Pieces per bag first."}>
              <Input type="number" step="0.001" value={form.bunch_per_bag ?? ""} disabled={bagFactor <= 0}
                     onChange={e=>setBunchFromBag(e.target.value)} data-testid="sku-form-bunch-per-bag"/>
            </Field>
          )}
          <div className="col-span-2">
            <Field label="Barcode"><Input value={form.barcode} onChange={e=>patch("barcode", e.target.value)} data-testid="sku-form-barcode"/></Field>
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.is_orderable}
                        onCheckedChange={(v) => patch("is_orderable", v === true)}
                        data-testid="sku-form-is-orderable"/>
              Orderable directly by reps/distributors
            </label>
            <p className="text-xs text-neutral-500 mt-1">
              Uncheck for a pure scheme giveaway article (e.g. a Free Goods
              "Get SKU" that's never sold on its own) — it stays selectable
              in the Scheme form's Get SKU field, but won't appear in the
              normal order-entry SKU picker.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.sku_code || !form.product_id}
                  className="tulsi-gradient text-white font-semibold"
                  data-testid="sku-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : null}
            {isEdit ? "Save changes" : "Create SKU"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Pricing form (with slab editor)
// ---------------------------------------------------------------------------
export function PricingForm({ open, onOpenChange, onSaved }) {
  const skus = useCollection("/skus", { is_active: true });
  const zones = useCollection("/zones", {});
  const dists = useCollection("/distributors", { page_size: 2000 });
  const [form, setForm] = useState({
    sku_id: "", scope_type: "zone", scope_id: "",
    slabs: [{ min_qty: 1, max_qty: "", price: 0 }],
    valid_from: "", valid_to: "", reason: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setForm({
      sku_id: "", scope_type: "zone", scope_id: "",
      slabs: [{ min_qty: 1, max_qty: "", price: 0 }],
      valid_from: "", valid_to: "", reason: "",
    });
  }, [open]);

  const patch = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const patchSlab = (i, k, v) => setForm(s => ({
    ...s, slabs: s.slabs.map((row, idx) => idx === i ? { ...row, [k]: v } : row),
  }));
  const addSlab = () => setForm(s => ({
    ...s, slabs: [...s.slabs, {
      min_qty: (Number(s.slabs.at(-1)?.max_qty) || Number(s.slabs.at(-1)?.min_qty || 0)) + 1,
      max_qty: "", price: 0,
    }],
  }));
  const removeSlab = (i) => setForm(s => ({
    ...s, slabs: s.slabs.filter((_, idx) => idx !== i),
  }));

  const scopeOptions = form.scope_type === "zone" ? zones : dists;
  const scopeSelectOptions = scopeOptions.map(o => ({
    id: o.id, code: o.code, name: o.name,
  }));
  const skuOptions = skus.map(s => ({ id: s.id, code: s.sku_code, name: s.name || "" }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        sku_id: form.sku_id, scope_type: form.scope_type, scope_id: form.scope_id,
        slabs: form.slabs.map(s => ({
          min_qty: Number(s.min_qty),
          max_qty: s.max_qty === "" ? null : Number(s.max_qty),
          price: Number(s.price),
        })),
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        reason: form.reason || null,
      };
      const { data } = await api.post("/pricing", payload);
      toast.success(`Pricing created (${data.slabs.length} slab${data.slabs.length===1?"":"s"})`);
      onSaved?.(data); onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="pricing-form-dialog">
        <DialogHeader>
          <DialogTitle>New pricing entry</DialogTitle>
          <DialogDescription>
            Slabs let one SKU have quantity-tiered pricing. Distributor overrides zone overrides base price.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4">
          <Field label="SKU">
            <SearchableSelect
              value={form.sku_id}
              onChange={(v) => patch("sku_id", v)}
              options={skuOptions}
              placeholder="Search SKU by code or name…"
              testid="pricing-form-sku"
            />
          </Field>
          <Field label="Scope">
            <Select value={form.scope_type} onValueChange={v=>{patch("scope_type", v); patch("scope_id", "");}}>
              <SelectTrigger data-testid="pricing-form-scope-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="zone">Zone</SelectItem>
                <SelectItem value="distributor">Distributor</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={form.scope_type === "zone" ? "Zone" : "Distributor"}>
            <SearchableSelect
              value={form.scope_id}
              onChange={(v) => patch("scope_id", v)}
              options={scopeSelectOptions}
              placeholder={form.scope_type === "zone"
                ? "Search zone by code or name…"
                : "Search distributor by code or name…"}
              testid="pricing-form-scope-id"
            />
          </Field>
          <Field label="Valid from (ISO)">
            <Input value={form.valid_from} onChange={e=>patch("valid_from", e.target.value)}
                   placeholder="2026-01-01T00:00:00Z" data-testid="pricing-form-valid-from"/>
          </Field>
          <Field label="Valid to (ISO)">
            <Input value={form.valid_to} onChange={e=>patch("valid_to", e.target.value)}
                   placeholder="optional" data-testid="pricing-form-valid-to"/>
          </Field>
          <Field label="Change reason">
            <Input value={form.reason} onChange={e=>patch("reason", e.target.value)}
                   placeholder="optional" data-testid="pricing-form-reason"/>
          </Field>
        </div>

        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="pricing-form-slabs">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-800">Slabs</div>
            <Button size="sm" variant="outline" onClick={addSlab} data-testid="pricing-form-add-slab">
              <Plus className="h-3 w-3 mr-1"/> Add slab
            </Button>
          </div>
          <div className="text-[11px] text-neutral-500">
            min_qty starts at 1 for the first slab. Leave max_qty blank on the top slab for open-ended.
          </div>
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Min qty</Label>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Max qty (blank = ∞)</Label>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Price</Label>
            <span/>
            {form.slabs.map((s, i) => (
              <div key={i} className="col-span-4 grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`pricing-form-slab-row-${i}`}>
                <Input type="number" value={s.min_qty} onChange={e=>patchSlab(i, "min_qty", e.target.value)} data-testid={`pricing-form-slab-min-${i}`}/>
                <Input type="number" value={s.max_qty} onChange={e=>patchSlab(i, "max_qty", e.target.value)} data-testid={`pricing-form-slab-max-${i}`}/>
                <Input type="number" value={s.price} onChange={e=>patchSlab(i, "price", e.target.value)} data-testid={`pricing-form-slab-price-${i}`}/>
                <Button size="sm" variant="ghost" onClick={()=>removeSlab(i)} disabled={form.slabs.length === 1}
                        data-testid={`pricing-form-slab-del-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-500"/>
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.sku_id || !form.scope_id}
                  className="tulsi-gradient text-white font-semibold"
                  data-testid="pricing-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : null}
            Create pricing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Scheme form
// ---------------------------------------------------------------------------
const SCHEME_TYPES = [
  { code: "QPS_QTY", label: "Quantity purchase scheme (qty)" },
  { code: "QPS_VALUE", label: "Quantity purchase scheme (value)" },
  { code: "FREE_GOODS", label: "Free goods (buy X get Y)" },
  { code: "DISCOUNT_PCT", label: "% discount" },
  { code: "DISCOUNT_FLAT", label: "Flat amount discount" },
];

const EMPTY_SCHEME_FORM = {
  code: "", name: "", description: "", scheme_type: "QPS_QTY",
  order_channel: "SECONDARY",
  priority: 100, valid_from: "", valid_to: "", reason: "",
  zone_ids: [], sku_ids: [], outlet_type_lookup_ids: [], product_category_lookup_ids: [],
  distributor_ids: [], sub_zone_ids: [],
  conditions: { slabs: [{ min_qty: 10, benefit_qty: 1 }] },
};

export function SchemeForm({ open, onOpenChange, onSaved, existing = null }) {
  const skus = useCollection("/skus", { is_active: true });
  const zones = useCollection("/zones", {});
  const dists = useCollection("/distributors", { page_size: 2000 });
  const subZones = useCollection("/sub-zones", {});
  const outletTypes = useLookups("outlet_channel");
  const productCats = useLookups("product_category");
  const isEdit = !!existing?.id;

  const [form, setForm] = useState(EMPTY_SCHEME_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setForm(EMPTY_SCHEME_FORM); return; }
    if (existing) {
      const app = existing.applicability || {};
      setForm({
        code: existing.code || "", name: existing.name || "",
        description: existing.description || "", scheme_type: existing.scheme_type || "QPS_QTY",
        order_channel: existing.order_channel || "SECONDARY",
        priority: existing.priority ?? 100,
        valid_from: existing.valid_from || "", valid_to: existing.valid_to || "", reason: "",
        zone_ids: app.zone_ids || [], sku_ids: app.sku_ids || [],
        outlet_type_lookup_ids: app.outlet_type_lookup_ids || [],
        product_category_lookup_ids: app.product_category_lookup_ids || [],
        distributor_ids: app.distributor_ids || [],
        sub_zone_ids: app.sub_zone_ids || [],
        conditions: existing.conditions || {},
      });
    }
  }, [open, existing?.id]);

  const patch = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggle = (k, id) => setForm(s => ({
    ...s, [k]: s[k].includes(id) ? s[k].filter(x => x !== id) : [...s[k], id],
  }));

  // Reset conditions structure when scheme_type changes
  const onTypeChange = (t) => {
    let conditions = {};
    if (t === "QPS_QTY") conditions = { slabs: [{ min_qty: 10, benefit_qty: 1 }] };
    else if (t === "QPS_VALUE") conditions = { slabs: [{ min_value: 1000, benefit_qty: 1 }] };
    else if (t === "FREE_GOODS") conditions = { buy_sku_id: "", buy_qty: 10, get_sku_id: "", get_qty: 1 };
    else if (t === "DISCOUNT_PCT") conditions = { pct: 5 };
    else if (t === "DISCOUNT_FLAT") conditions = { flat_amount: 100 };
    setForm(s => ({ ...s, scheme_type: t, conditions }));
  };

  const patchCond = (k, v) => setForm(s => ({ ...s, conditions: { ...s.conditions, [k]: v } }));
  const patchCondSlab = (i, k, v) => setForm(s => ({
    ...s, conditions: {
      ...s.conditions,
      slabs: s.conditions.slabs.map((row, idx) => idx === i ? { ...row, [k]: k.includes("qty") || k.includes("value") ? Number(v) : v } : row),
    },
  }));
  const addCondSlab = () => setForm(s => ({
    ...s, conditions: { ...s.conditions, slabs: [...(s.conditions.slabs || []), { min_qty: 0, benefit_qty: 0 }] },
  }));
  const removeCondSlab = (i) => setForm(s => ({
    ...s, conditions: { ...s.conditions, slabs: s.conditions.slabs.filter((_, idx) => idx !== i) },
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(), name: form.name, description: form.description || null,
        scheme_type: form.scheme_type,
        order_channel: form.order_channel,
        applicability: {
          zone_ids: form.zone_ids, sku_ids: form.sku_ids,
          outlet_type_lookup_ids: form.outlet_type_lookup_ids,
          product_category_lookup_ids: form.product_category_lookup_ids,
          distributor_ids: form.distributor_ids,
          sub_zone_ids: form.sub_zone_ids,
        },
        conditions: form.conditions,
        valid_from: form.valid_from || null, valid_to: form.valid_to || null,
        priority: Number(form.priority) || 100,
        reason: form.reason || null,
      };
      if (isEdit) {
        // code is immutable once created — the backend's SchemeUpdate model
        // does not accept it.
        const { code, ...rest } = payload;
        const { data } = await api.patch(`/schemes/${existing.id}`, rest);
        toast.success(`Scheme ${data.code} updated`);
        onSaved?.(data); onOpenChange(false);
      } else {
        const { data } = await api.post("/schemes", payload);
        toast.success(`Scheme ${data.code} created`);
        onSaved?.(data); onOpenChange(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const ConditionsEditor = () => {
    const t = form.scheme_type;
    if (t === "QPS_QTY" || t === "QPS_VALUE") {
      const isQty = t === "QPS_QTY";
      return (
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="scheme-form-conditions">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Slabs ({isQty ? "quantity-based" : "value-based"})</div>
            <Button size="sm" variant="outline" onClick={addCondSlab} data-testid="scheme-form-cond-add-slab">
              <Plus className="h-3 w-3 mr-1"/> Add slab
            </Button>
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">
              {isQty ? "Min qty" : "Min value (₹)"}
            </Label>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Benefit qty</Label>
            <span/>
            {form.conditions.slabs?.map((s, i) => (
              <div key={i} className="col-span-3 grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input type="number" value={s[isQty ? "min_qty" : "min_value"]}
                       onChange={e=>patchCondSlab(i, isQty ? "min_qty" : "min_value", e.target.value)}
                       data-testid={`scheme-form-slab-min-${i}`}/>
                <Input type="number" value={s.benefit_qty || 0}
                       onChange={e=>patchCondSlab(i, "benefit_qty", e.target.value)}
                       data-testid={`scheme-form-slab-benefit-${i}`}/>
                <Button size="sm" variant="ghost" onClick={()=>removeCondSlab(i)}
                        data-testid={`scheme-form-slab-del-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-500"/>
                </Button>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (t === "FREE_GOODS") {
      // Stored conditions.buy_qty/get_qty are always in PIECES (that's what
      // the backend's scheme-evaluation math compares an order line's own
      // qty_pcs against — see routes_orders.py's _apply_scheme_to_line).
      // Typing that number out by hand means converting from kg yourself
      // every time, which is exactly the "complicated" part — so this Kg
      // box is a second, linked view of the SAME value: type kg, pieces
      // fill in automatically (needs that SKU's own Weight (kg) set), same
      // pattern as the SKU form's own Kg/Pcs/Bag price trio.
      const skuWeight = (id) => skus.find(s => s.id === id)?.weight_per_pc_kg || 0;
      const buyWeight = skuWeight(form.conditions.buy_sku_id);
      const getWeight = skuWeight(form.conditions.get_sku_id);
      const skuLabel = (s) => [s.product_name || s.pack_variant_label, s.sku_code].filter(Boolean).join(" — ");
      return (
        <div className="grid grid-cols-2 gap-3" data-testid="scheme-form-conditions">
          <Field label="Buy SKU">
            <SearchableSelect
              value={form.conditions.buy_sku_id}
              onChange={v=>patchCond("buy_sku_id", v)}
              options={skus.map(s => ({ id: s.id, label: skuLabel(s) }))}
              placeholder="Select…" testid="scheme-form-buy-sku"
            />
          </Field>
          <Field label="Get SKU">
            <SearchableSelect
              value={form.conditions.get_sku_id}
              onChange={v=>patchCond("get_sku_id", v)}
              options={skus.map(s => ({ id: s.id, label: skuLabel(s) }))}
              placeholder="Select…" testid="scheme-form-get-sku"
            />
          </Field>
          <Field label="Buy qty (kg)"
                 help={buyWeight > 0 ? "Auto-synced with Buy qty (pcs)." : "Pick a Buy SKU with Weight (kg) set to enter this in kg."}>
            <Input type="number" disabled={buyWeight <= 0}
                   value={buyWeight > 0 ? round2((form.conditions.buy_qty || 0) * buyWeight) : ""}
                   onChange={e => patchCond("buy_qty",
                     buyWeight > 0 ? Math.round(Number(e.target.value) / buyWeight) : form.conditions.buy_qty)}
                   data-testid="scheme-form-buy-qty-kg"/>
          </Field>
          <Field label="Get qty (kg)"
                 help={getWeight > 0 ? "Auto-synced with Get qty (pcs)." : "Pick a Get SKU with Weight (kg) set to enter this in kg."}>
            <Input type="number" disabled={getWeight <= 0}
                   value={getWeight > 0 ? round2((form.conditions.get_qty || 0) * getWeight) : ""}
                   onChange={e => patchCond("get_qty",
                     getWeight > 0 ? Math.round(Number(e.target.value) / getWeight) : form.conditions.get_qty)}
                   data-testid="scheme-form-get-qty-kg"/>
          </Field>
          <Field label="Buy qty (pcs)" help="The exact stored trigger — same value as Buy qty (kg) above.">
            <Input type="number" value={form.conditions.buy_qty}
                   onChange={e=>patchCond("buy_qty", Number(e.target.value))}
                   data-testid="scheme-form-buy-qty"/>
          </Field>
          <Field label="Get qty (pcs)" help="The exact stored free quantity — same value as Get qty (kg) above.">
            <Input type="number" value={form.conditions.get_qty}
                   onChange={e=>patchCond("get_qty", Number(e.target.value))}
                   data-testid="scheme-form-get-qty"/>
          </Field>
        </div>
      );
    }
    if (t === "DISCOUNT_PCT") {
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Discount %">
            <Input type="number" value={form.conditions.pct}
                   onChange={e=>patchCond("pct", Number(e.target.value))}
                   data-testid="scheme-form-pct"/>
          </Field>
        </div>
      );
    }
    if (t === "DISCOUNT_FLAT") {
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Flat amount (₹)">
            <Input type="number" value={form.conditions.flat_amount}
                   onChange={e=>patchCond("flat_amount", Number(e.target.value))}
                   data-testid="scheme-form-flat"/>
          </Field>
        </div>
      );
    }
    return null;
  };

  const MultiToggle = ({ label, items, field, testid, labelKey = "code", extraKey = "name", searchable = false, help }) => {
    const [q, setQ] = useState("");
    const shown = searchable && q.trim()
      ? items.filter(i => (i.label || i[labelKey] || i[extraKey] || "").toLowerCase().includes(q.trim().toLowerCase()))
      : items;
    const selectAllShown = () => setForm(s => ({
      ...s, [field]: Array.from(new Set([...s[field], ...shown.map(i => i.id)])),
    }));
    const clearShown = () => {
      const shownIds = new Set(shown.map(i => i.id));
      setForm(s => ({ ...s, [field]: s[field].filter(id => !shownIds.has(id)) }));
    };
    return (
    <Field label={label} testid={testid} help={help}>
      {searchable && (
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
               className="h-8 text-xs mb-1.5" data-testid={`${testid}-search`}/>
      )}
      <div className="flex items-center gap-2 mb-1">
        <button type="button" onClick={selectAllShown}
                className="text-[10px] text-[#6D2C91] font-semibold underline decoration-dotted"
                data-testid={`${testid}-select-all`}>
          Select all{q.trim() ? ` (${shown.length})` : ""}
        </button>
        <button type="button" onClick={clearShown}
                className="text-[10px] text-neutral-500 font-semibold underline decoration-dotted"
                data-testid={`${testid}-clear-all`}>
          Clear{q.trim() ? " shown" : " all"}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-[92px] overflow-y-auto p-2 border border-neutral-200 rounded-md">
        {shown.length === 0 && <span className="text-[11px] text-neutral-400">No items</span>}
        {shown.map(i => {
          const active = form[field].includes(i.id);
          return (
            <button key={i.id} type="button" onClick={() => toggle(field, i.id)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      active ? "tulsi-gradient text-white border-transparent" :
                      "bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100"}`}
                    data-testid={`${testid}-${i.id}`}>
              {i.label || i[labelKey] || i[extraKey]}
              {active && <X className="inline h-3 w-3 ml-1"/>}
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-neutral-500 mt-1">
        Empty = applies to all
      </div>
    </Field>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scheme-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit scheme ${existing?.code || ""}` : "New scheme"}</DialogTitle>
          <DialogDescription>Promotional rules with applicability filters and conditions.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Code"><Input value={form.code} disabled={isEdit} onChange={e=>patch("code", e.target.value.toUpperCase())} data-testid="scheme-form-code"/></Field>
          <Field label="Name"><Input value={form.name} onChange={e=>patch("name", e.target.value)} data-testid="scheme-form-name"/></Field>
          <Field label="Priority">
            <Input type="number" value={form.priority} onChange={e=>patch("priority", e.target.value)} data-testid="scheme-form-priority"/>
          </Field>
          <div className="col-span-3">
            <Field label="Description">
              <Textarea value={form.description} onChange={e=>patch("description", e.target.value)}
                        data-testid="scheme-form-desc"/>
            </Field>
          </div>
          <Field label="Scheme type">
            <Select value={form.scheme_type} onValueChange={onTypeChange}>
              <SelectTrigger data-testid="scheme-form-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                {SCHEME_TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Order channel"
                 help="Which order flow this scheme fires on — primary (distributor→company) and secondary (retailer) orders are priced separately.">
            <Select value={form.order_channel} onValueChange={(v) => patch("order_channel", v)}>
              <SelectTrigger data-testid="scheme-form-channel"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="SECONDARY">Secondary (retailer orders)</SelectItem>
                <SelectItem value="PRIMARY">Primary (distributor → company)</SelectItem>
                <SelectItem value="BOTH">Both</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Valid from (ISO)">
            <Input value={form.valid_from} onChange={e=>patch("valid_from", e.target.value)}
                   placeholder="2026-01-01" data-testid="scheme-form-valid-from"/>
          </Field>
          <Field label="Valid to (ISO)">
            <Input value={form.valid_to} onChange={e=>patch("valid_to", e.target.value)}
                   placeholder="optional" data-testid="scheme-form-valid-to"/>
          </Field>
        </div>

        <div className="text-sm font-semibold text-neutral-800 border-t pt-3">Applicability</div>
        <div className="grid grid-cols-2 gap-3">
          <MultiToggle label="Zones" items={zones} field="zone_ids" testid="scheme-form-zones"/>
          <MultiToggle label="Outlet types" items={outletTypes.items} field="outlet_type_lookup_ids" testid="scheme-form-outlets"/>
          <MultiToggle label="Product categories" items={productCats.items} field="product_category_lookup_ids" testid="scheme-form-cats"/>
          <MultiToggle label="Sub-zones"
                       help="Applies to every distributor in the picked sub-zone(s) — most schemes are assigned this way rather than distributor-by-distributor."
                       items={subZones.map(z => ({ id: z.id, label: [z.code, z.name].filter(Boolean).join(" — ") }))}
                       field="sub_zone_ids" testid="scheme-form-subzones" searchable/>
          <MultiToggle label="Distributors"
                       help={form.sub_zone_ids.length > 0
                         ? "Narrowed to distributors mapped to the sub-zone(s) picked above."
                         : undefined}
                       items={(form.sub_zone_ids.length > 0
                         ? dists.filter(d => form.sub_zone_ids.includes(d.sub_zone_id))
                         : dists).map(d => ({ id: d.id, label: [d.code, d.name].filter(Boolean).join(" — ") }))}
                       field="distributor_ids" testid="scheme-form-distributors" searchable/>
          <MultiToggle label="Specific SKUs"
                       items={skus.map(s=>({id: s.id, label: [s.product_name || s.pack_variant_label, s.sku_code].filter(Boolean).join(" — ")}))}
                       field="sku_ids" testid="scheme-form-skus"/>
        </div>

        <div className="text-sm font-semibold text-neutral-800 border-t pt-3">Conditions</div>
        <ConditionsEditor/>

        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.code || !form.name}
                  className="tulsi-gradient text-white font-semibold"
                  data-testid="scheme-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : null}
            {isEdit ? "Save changes" : "Create scheme"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Simple Scheme form — a plain-language wizard for the common case ("buy
// this many Kg/Bunch/Bags of a SKU, get a free item or a flat discount")
// instead of the full Scheme form's scheme-type + raw JSON-conditions
// model. Every SKU row here is submitted as ITS OWN scheme record behind
// the scenes (POST /api/schemes, scheme_type=FREE_GOODS) — each SKU's
// quantity threshold triggers independently, never pooled together (per
// user's confirmed intent). Free-item and discount rewards both reuse the
// same buy_sku_id/buy_qty trigger on FREE_GOODS; discount_amount is a
// small backend extension (routes_orders.py's _apply_scheme_to_line) for
// exactly this "qty-gated flat discount" case, which no other scheme_type
// supports (DISCOUNT_FLAT/PCT have no qty trigger at all).
// ---------------------------------------------------------------------------
const QTY_UNITS = [
  { code: "KG", label: "Kg" },
  { code: "BAG", label: "Bag" },
  { code: "BUNCH", label: "Bunch" },
  { code: "PCS", label: "Pcs" },
];

function skuDisplayLabel(s) {
  return [s.product_name || s.pack_variant_label, s.sku_code].filter(Boolean).join(" — ");
}

function skuUnitFactor(sku, unit) {
  if (!sku) return 0;
  if (unit === "PCS") return 1;
  if (unit === "KG") return sku.weight_per_pc_kg || 0;
  if (unit === "BAG") return sku.bag_conversion_factor || 0;
  if (unit === "BUNCH") return sku.bunch_conversion_factor || 0;
  return 0;
}

// qty (in `unit`) -> canonical pieces, the only unit the backend's scheme
// engine actually compares against (order lines are always canonicalised
// to qty_pcs before scheme evaluation — see routes_orders.py).
function toPieces(sku, qty, unit) {
  const n = Number(qty);
  if (!sku || !n) return 0;
  if (unit === "PCS") return Math.round(n);
  const factor = skuUnitFactor(sku, unit);
  if (!factor) return 0;
  return unit === "KG" ? Math.round(n / factor) : Math.round(n * factor);
}

// Pieces needed to reach a "buy" threshold: for a Kg threshold round UP, so the
// scheme never fires below the kg the admin typed (nearest-rounding made 1 kg of a
// 0.44 kg pack fire at 0.88 kg). The exact kg is also stored (buy_qty_kg) and is
// what the order engine compares against.
function toBuyPieces(sku, qty, unit) {
  const n = Number(qty);
  const wpc = sku?.weight_per_pc_kg || 0;
  if (unit === "KG" && n > 0 && wpc > 0) return Math.max(1, Math.ceil(n / wpc - 1e-9));
  return toPieces(sku, qty, unit);
}

// Inverse of toPieces, for re-populating the form when editing an existing
// scheme — prefer showing the friendliest unit (Kg) the SKU supports, same
// default the row/free-item pickers reset to when a SKU is first chosen.
function fromPieces(sku, pieces) {
  const n = Number(pieces) || 0;
  if (!sku || n <= 0) return { qty: "", unit: "PCS" };
  const wpc = sku.weight_per_pc_kg || 0;
  if (wpc > 0) return { qty: String(Math.round(n * wpc * 1000) / 1000), unit: "KG" };
  return { qty: String(n), unit: "PCS" };
}

export function SimpleSchemeForm({ open, onOpenChange, onSaved, existing = null }) {
  const skus = useCollection("/skus", { is_active: true });
  const dists = useCollection("/distributors", { page_size: 2000 });
  const subZones = useCollection("/sub-zones", {});
  const emptyRow = () => ({ sku_id: "", qty: "", unit: "KG" });
  const isEdit = !!existing?.id;

  const [name, setName] = useState("");
  const [orderChannel, setOrderChannel] = useState("SECONDARY");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  // Empty = applies to every distributor (matches the backend's "empty
  // filter = no restriction" convention for zone/outlet/category too).
  // Non-empty restricts the scheme to only these distributors — for
  // schemes that are not eligible across the board. Most schemes are
  // actually assigned by sub-zone (every distributor in it), not picked
  // distributor-by-distributor, so that's a separate, independent filter —
  // both narrow the same pool further when both are set.
  const [distributorIds, setDistributorIds] = useState([]);
  const [distSearch, setDistSearch] = useState("");
  const [subZoneIds, setSubZoneIds] = useState([]);
  const [subZoneSearch, setSubZoneSearch] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  // Bulk SKU picker — tick several SKUs at once and add them all as rows in
  // one go, instead of hitting "Add SKU" and re-opening the row dropdown for
  // every single one. Each ticked SKU still gets its own row/qty/unit
  // afterwards, same as adding them one at a time.
  const [skuPickerIds, setSkuPickerIds] = useState([]);
  const [skuPickerSearch, setSkuPickerSearch] = useState("");
  const [rewardType, setRewardType] = useState("FREE_ITEM"); // FREE_ITEM | FREE_SKU | DISCOUNT
  // Free item is plain text, not a SKU pick — most giveaways (a coupon, a
  // bottle, a printed voucher) are not inventory-tracked at all, so forcing
  // a SKU record (with its own weight/bag/bunch setup) for every one-off
  // freebie was exactly the "complicated" friction this form exists to
  // remove. The description is recorded on the order line so it is visible
  // on order detail/PDF; no companion stock-deducting line is created.
  const [freeItemText, setFreeItemText] = useState("");
  // Free SKU is the tracked alternative — a real companion order line is
  // created for an actual SKU (get_sku_id/get_qty), deducted from that
  // SKU's own stock. Use this when the giveaway is itself a stocked item
  // (e.g. free Jeeru from inventory), not a one-off freebie.
  const [freeSkuId, setFreeSkuId] = useState("");
  const [freeQty, setFreeQty] = useState("");
  const [freeUnit, setFreeUnit] = useState("KG");
  const [discountAmount, setDiscountAmount] = useState("");
  const [busy, setBusy] = useState(false);
  // The simple wizard creates one scheme record per SKU — editing must load
  // and manage ALL of them together (not just the one row's Edit button was
  // clicked from), so this "one scheme" is actually manageable as one thing
  // instead of N separate list rows. `groupId` is the shared grouping key
  // written on save (backfilled for legacy groups that predate the field);
  // `originalSchemeIds` remembers which records existed at load time so a
  // row removed before saving gets deactivated, not just dropped silently.
  const [groupId, setGroupId] = useState(null);
  const [originalSchemeIds, setOriginalSchemeIds] = useState([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);
  // Tracks which scheme id the sibling list has already been loaded for —
  // the load effect's dependency array includes `skus.length` (needed so
  // it waits for the SKU collection to be ready), which means it re-runs
  // every time that collection's length changes for any reason. Without
  // this guard, each re-run redoes the whole fetch-and-populate sequence.
  const loadedSiblingsForRef = useRef(null);
  // "Duplicate as new scheme" — same SKUs/distributors/sub-zones, but a
  // different eligible qty and/or reward, without re-picking everything
  // from scratch. Flips this open-for-edit dialog into create mode (all
  // current field values kept, scheme_id/group linkage cleared) so Save
  // creates fresh records instead of updating the ones being viewed.
  const [duplicating, setDuplicating] = useState(false);
  const effectiveIsEdit = isEdit && !duplicating;

  useEffect(() => {
    if (!open) {
      setName(""); setOrderChannel("SECONDARY"); setValidFrom(""); setValidTo("");
      setDistributorIds([]); setDistSearch("");
      setSubZoneIds([]); setSubZoneSearch("");
      setSkuPickerIds([]); setSkuPickerSearch("");
      setRows([emptyRow()]); setRewardType("FREE_ITEM");
      setFreeItemText(""); setFreeSkuId(""); setFreeQty(""); setFreeUnit("KG");
      setDiscountAmount(""); setGroupId(null); setOriginalSchemeIds([]); setSiblingsLoading(false);
      setDuplicating(false);
      loadedSiblingsForRef.current = null;
      return;
    }
    if (!existing) return;
    if (loadedSiblingsForRef.current === existing.id) return;
    // Editing existing scheme record(s) — populate every field back from
    // stored conditions/applicability. Waits for `skus` to load (needed to
    // know each SKU's weight_per_pc_kg for the pieces->kg display
    // conversion) before resolving the SKU-dependent fields.
    if (skus.length === 0) return;
    const c = existing.conditions || {};
    const app = existing.applicability || {};
    setName(existing.name || "");
    setOrderChannel(existing.order_channel || "SECONDARY");
    setValidFrom((existing.valid_from || "").slice(0, 10));
    setValidTo((existing.valid_to || "").slice(0, 10));
    setDistributorIds(app.distributor_ids || []);
    setSubZoneIds(app.sub_zone_ids || []);
    if (c.free_item_note) {
      setRewardType("FREE_ITEM"); setFreeItemText(c.free_item_note);
    } else if (c.get_sku_id) {
      setRewardType("FREE_SKU");
      const freeSku = skus.find(s => s.id === c.get_sku_id);
      const freeDisplay = fromPieces(freeSku, c.get_qty);
      setFreeSkuId(c.get_sku_id); setFreeQty(freeDisplay.qty); setFreeUnit(freeDisplay.unit);
    } else if (c.discount_amount != null) {
      setRewardType("DISCOUNT"); setDiscountAmount(String(c.discount_amount));
    }

    const rowFromScheme = (s) => {
      const sc = s.conditions || {};
      const sku = skus.find(x => x.id === sc.buy_sku_id);
      const disp = sc.buy_qty_kg ? { qty: String(sc.buy_qty_kg), unit: "KG" } : fromPieces(sku, sc.buy_qty);
      return { sku_id: sc.buy_sku_id || "", qty: disp.qty, unit: disp.unit, scheme_id: s.id };
    };

    let cancelled = false;
    setSiblingsLoading(true);
    (async () => {
      let siblings = [existing];
      try {
        if (existing.scheme_group_id) {
          const { data } = await api.get("/schemes", {
            // /schemes caps page_size at 100 server-side — 200 here used to
            // fail validation entirely once routing was fixed to actually
            // reach that endpoint (see routes_masters_read.py's removed
            // shadow handler), silently falling back to a 1-row "sibling"
            // list on every edit-open.
            params: { group_id: existing.scheme_group_id, page_size: 100, is_active: true },
          });
          if (data.items?.length) siblings = data.items;
        } else {
          // Legacy group (predates scheme_group_id) — fall back to an exact
          // name + channel + "simple wizard shape" match. The name search
          // is a substring candidate fetch; exact equality below prunes
          // any unrelated scheme that merely contains the same text.
          const { data } = await api.get("/schemes", {
            params: { q: existing.name, page_size: 100, is_active: true },
          });
          const matches = (data.items || []).filter(s =>
            s.name === existing.name && s.order_channel === existing.order_channel
            && (s.ui_source === "simple" || looksLikeSimpleScheme(s)));
          if (matches.length) siblings = matches;
        }
      } catch { /* fall back to just `existing` below */ }
      if (cancelled) return;
      if (!siblings.some(s => s.id === existing.id)) siblings = [existing, ...siblings];
      // Defensive dedupe by id — this effect re-runs whenever `skus.length`
      // changes (e.g. the SKU collection loading in behind it), and with
      // React StrictMode's deliberate double-invoke of effects in dev, two
      // overlapping fetches can both land; without this, a scheme's full
      // SKU list would silently double up in the form (every row appearing
      // twice) despite the API itself returning correctly deduplicated data.
      const seen = new Set();
      siblings = siblings.filter(s => (seen.has(s.id) ? false : (seen.add(s.id), true)));
      setGroupId(existing.scheme_group_id || null);
      setOriginalSchemeIds(siblings.map(s => s.id));
      setRows(siblings.map(rowFromScheme));
      setSiblingsLoading(false);
      loadedSiblingsForRef.current = existing.id;
    })();
    return () => { cancelled = true; };
  }, [open, existing?.id, skus.length]);

  const skuById = (id) => skus.find(s => s.id === id);
  const unitOptionsFor = (sku) => QTY_UNITS.filter(u => u.code === "PCS" || skuUnitFactor(sku, u.code) > 0);

  const patchRow = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => [...rs, emptyRow()]);
  const removeRow = (i) => setRows(rs => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);
  const toggleDistributor = (id) => setDistributorIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const toggleSubZone = (id) => setSubZoneIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const toggleSkuPicker = (id) => setSkuPickerIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const shownSkuPicker = skus.filter(s => !skuPickerSearch.trim()
    || skuDisplayLabel(s).toLowerCase().includes(skuPickerSearch.trim().toLowerCase()));
  // "Select all" with no search active operates on the WHOLE catalog — an
  // easy accidental click that silently balloons a scheme to dozens of
  // unrelated SKUs (and, since a group shares one name/reward, quietly
  // renames/reshapes an existing scheme into something unrecognizable).
  // Confirm before selecting a large, unfiltered batch; a search-narrowed
  // selection is exactly what "select all" is for and doesn't need one.
  const selectAllShownSkuPicker = () => {
    if (!skuPickerSearch.trim() && shownSkuPicker.length > 10
        && !window.confirm(`Select all ${shownSkuPicker.length} SKUs in the catalog? Search first to narrow this down if you only meant a few.`)) {
      return;
    }
    setSkuPickerIds(ids => Array.from(new Set([...ids, ...shownSkuPicker.map(s => s.id)])));
  };
  const clearShownSkuPicker = () => { const s = new Set(shownSkuPicker.map(x => x.id)); setSkuPickerIds(ids => ids.filter(id => !s.has(id))); };
  const addPickedSkus = () => {
    const existingIds = new Set(rows.map(r => r.sku_id).filter(Boolean));
    const toAdd = skuPickerIds.filter(id => !existingIds.has(id));
    if (toAdd.length === 0) { setSkuPickerIds([]); return; }
    if (toAdd.length > 10
        && !window.confirm(`Add ${toAdd.length} SKUs to this scheme? They'll all share the same name and reward you set below.`)) {
      return;
    }
    setRows(rs => {
      // Drop any still-blank rows (e.g. the default empty one) so bulk-add
      // doesn't leave stray unused rows mixed in with the new ones.
      const base = rs.filter(r => r.sku_id);
      return [...base, ...toAdd.map(id => ({ sku_id: id, qty: "", unit: "KG" }))];
    });
    setSkuPickerIds([]);
  };

  // Once a sub-zone is picked above, narrow the distributor list to just its
  // mapped distributors — picking a sub-zone already restricts the scheme to
  // those distributors, so showing all 200+ of them here is just noise (and
  // "Select all" would otherwise select ones outside the sub-zone too).
  const distsInScope = subZoneIds.length > 0
    ? dists.filter(d => subZoneIds.includes(d.sub_zone_id))
    : dists;
  const shownDists = distsInScope.filter(d => !distSearch.trim()
    || [d.code, d.name].filter(Boolean).join(" ").toLowerCase().includes(distSearch.trim().toLowerCase()));
  const shownSubZones = subZones.filter(z => !subZoneSearch.trim()
    || [z.code, z.name].filter(Boolean).join(" ").toLowerCase().includes(subZoneSearch.trim().toLowerCase()));
  const selectAllShownDists = () => setDistributorIds(ids => Array.from(new Set([...ids, ...shownDists.map(d => d.id)])));
  const clearShownDists = () => { const s = new Set(shownDists.map(d => d.id)); setDistributorIds(ids => ids.filter(id => !s.has(id))); };
  const selectAllShownSubZones = () => setSubZoneIds(ids => Array.from(new Set([...ids, ...shownSubZones.map(z => z.id)])));
  const clearShownSubZones = () => { const s = new Set(shownSubZones.map(z => z.id)); setSubZoneIds(ids => ids.filter(id => !s.has(id))); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Scheme name is required"); return; }
    const validRows = rows.filter(r => r.sku_id && Number(r.qty) > 0);
    if (validRows.length === 0) { toast.error("Add at least one SKU with a quantity"); return; }
    if (rewardType === "FREE_ITEM") {
      if (!freeItemText.trim()) { toast.error("Describe the free item"); return; }
    } else if (rewardType === "FREE_SKU") {
      if (!freeSkuId || !(Number(freeQty) > 0)) { toast.error("Select the free SKU and its quantity"); return; }
    } else if (!(Number(discountAmount) > 0)) {
      toast.error("Enter the discount amount"); return;
    }

    let freeSkuPieces = 0;
    if (rewardType === "FREE_SKU") {
      freeSkuPieces = toPieces(skuById(freeSkuId), freeQty, freeUnit);
      if (freeSkuPieces <= 0) {
        toast.error(`Could not convert ${freeQty} ${freeUnit} for the free SKU (check its conversion factor)`);
        return;
      }
    }

    // Exact kg the admin typed (+ the pack weight it was computed with) so the
    // eligible quantity is the kg entered, not a rounded number of pieces.
    const kgExtra = (row) => {
      const w = skuById(row.sku_id)?.weight_per_pc_kg || 0;
      return row.unit === "KG" && w > 0 && Number(row.qty) > 0 ? { buy_qty_kg: Number(row.qty), buy_wpc: w } : {};
    };
    const buildConditions = (row, buyPieces) => rewardType === "FREE_ITEM"
      ? { buy_sku_id: row.sku_id, buy_qty: buyPieces, ...kgExtra(row), free_item_note: freeItemText.trim() }
      : rewardType === "FREE_SKU"
      ? { buy_sku_id: row.sku_id, buy_qty: buyPieces, ...kgExtra(row), get_sku_id: freeSkuId, get_qty: freeSkuPieces }
      : { buy_sku_id: row.sku_id, buy_qty: buyPieces, ...kgExtra(row), discount_amount: Number(discountAmount) };

    setBusy(true);
    const codeBase = (name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24)) || "SCHEME";
    const stamp = Date.now().toString(36).toUpperCase();

    if (effectiveIsEdit) {
      // Manage the WHOLE group as one thing: every row with a `scheme_id`
      // is an existing record to PATCH in place (immutable code/scheme_type
      // untouched); rows without one are new SKUs the user added, POSTed
      // fresh; any original sibling no longer present in `rows` (removed
      // via the row's delete button) gets deactivated. `groupId` backfills
      // scheme_group_id onto legacy groups (created before that field
      // existed) the first time they're edited, so it's a properly tracked
      // group from here on.
      const thisGroupId = groupId || (crypto.randomUUID?.() || stamp);
      const existingApp = existing.applicability || {};
      const sharedApplicability = { ...existingApp, distributor_ids: distributorIds, sub_zone_ids: subZoneIds };

      let updated = 0, created = 0, removed = 0;
      const failed = [];
      const keptIds = new Set(validRows.map(r => r.scheme_id).filter(Boolean));

      try {
        for (const row of validRows) {
          const rowSku = skuById(row.sku_id);
          const rowPieces = toBuyPieces(rowSku, row.qty, row.unit);
          if (rowPieces <= 0) {
            failed.push(`${rowSku ? skuDisplayLabel(rowSku) : row.sku_id} — could not convert ${row.qty} ${row.unit} (check its conversion factor)`);
            continue;
          }
          const conditions = buildConditions(row, rowPieces);
          if (row.scheme_id) {
            try {
              await api.patch(`/schemes/${row.scheme_id}`, {
                name: name.trim(), order_channel: orderChannel,
                applicability: sharedApplicability, conditions,
                valid_from: validFrom || null, valid_to: validTo || null,
                scheme_group_id: thisGroupId,
              });
              updated += 1;
            } catch (e) {
              failed.push(`${skuDisplayLabel(rowSku)} — ${formatApiError(e)}`);
            }
          } else {
            try {
              await api.post("/schemes", {
                code: `${codeBase}-${rowSku.sku_code}-${stamp}`,
                name: name.trim(), description: null,
                scheme_type: "FREE_GOODS", order_channel: orderChannel,
                applicability: { zone_ids: [], sku_ids: [], outlet_type_lookup_ids: [], product_category_lookup_ids: [],
                                 distributor_ids: distributorIds, sub_zone_ids: subZoneIds },
                conditions, valid_from: validFrom || null, valid_to: validTo || null,
                priority: 100, reason: null, ui_source: "simple", scheme_group_id: thisGroupId,
              });
              created += 1;
            } catch (e) {
              failed.push(`${skuDisplayLabel(rowSku)} — ${formatApiError(e)}`);
            }
          }
        }
        for (const id of originalSchemeIds) {
          if (keptIds.has(id)) continue;
          try {
            await api.delete(`/schemes/${id}`);
            removed += 1;
          } catch (e) {
            failed.push(`Removing a SKU — ${formatApiError(e)}`);
          }
        }
        const parts = [];
        if (updated > 0) parts.push(`${updated} updated`);
        if (created > 0) parts.push(`${created} added`);
        if (removed > 0) parts.push(`${removed} removed`);
        toast.success(`Scheme "${name.trim()}" saved — ${parts.join(", ") || "no changes"}`);
        if (failed.length) toast.error(`${failed.length} row(s) failed: ${failed.join("; ")}`);
        onSaved?.(); onOpenChange(false);
      } finally { setBusy(false); }
      return;
    }

    // Fresh group id for every plain create (including a "Duplicate as new
    // scheme" save) — guarantees this new batch is never accidentally
    // merged with an unrelated group that happens to share the same name
    // (the legacy fallback grouping heuristic matches on exact name, which
    // duplicating deliberately keeps similar to the original).
    const newGroupId = crypto.randomUUID?.() || stamp;
    let created = 0;
    const failed = [];
    try {
      for (const row of validRows) {
        const buySku = skuById(row.sku_id);
        const buyPieces = toBuyPieces(buySku, row.qty, row.unit);
        if (buyPieces <= 0) {
          failed.push(`${buySku ? skuDisplayLabel(buySku) : row.sku_id} — could not convert ${row.qty} ${row.unit} (check its conversion factor)`);
          continue;
        }
        const conditions = buildConditions(row, buyPieces);
        const payload = {
          code: `${codeBase}-${buySku.sku_code}-${stamp}`,
          name: name.trim(), description: null,
          scheme_type: "FREE_GOODS", order_channel: orderChannel,
          applicability: { zone_ids: [], sku_ids: [], outlet_type_lookup_ids: [], product_category_lookup_ids: [],
                           distributor_ids: distributorIds, sub_zone_ids: subZoneIds },
          conditions, valid_from: validFrom || null, valid_to: validTo || null,
          priority: 100, reason: null, ui_source: "simple", scheme_group_id: newGroupId,
        };
        try {
          await api.post("/schemes", payload);
          created += 1;
        } catch (e) {
          failed.push(`${skuDisplayLabel(buySku)} — ${formatApiError(e)}`);
        }
      }
      if (created > 0) toast.success(`Created ${created} scheme record${created === 1 ? "" : "s"} (one per SKU)`);
      if (failed.length) toast.error(`${failed.length} row(s) failed: ${failed.join("; ")}`);
      if (created > 0) { onSaved?.(); onOpenChange(false); }
    } finally { setBusy(false); }
  };

  // Keeps every current field (SKUs, qty, reward, distributors, sub-zones,
  // dates) but detaches from the record(s) being edited — Save then
  // creates brand-new scheme record(s) instead of updating the originals.
  // Exactly what "same SKU/distributor, different qty & reward" needs
  // without re-picking distributors/sub-zones/SKUs from scratch.
  const duplicateAsNew = () => {
    setDuplicating(true);
    setGroupId(null);
    setOriginalSchemeIds([]);
    setRows(rs => rs.map(r => ({ sku_id: r.sku_id, qty: r.qty, unit: r.unit })));
    setName(n => {
      const trimmed = n.trim();
      return trimmed && !/\(copy\)$/i.test(trimmed) ? `${trimmed} (Copy)` : trimmed;
    });
  };

  const deactivateWholeScheme = async () => {
    if (!window.confirm(`Deactivate "${name}" — all ${originalSchemeIds.length} SKU record${originalSchemeIds.length === 1 ? "" : "s"}? This can be reversed from the scheme's own row later.`)) return;
    setBusy(true);
    let removed = 0;
    const failed = [];
    try {
      for (const id of originalSchemeIds) {
        try {
          await api.delete(`/schemes/${id}`);
          removed += 1;
        } catch (e) {
          failed.push(formatApiError(e));
        }
      }
      if (removed > 0) toast.success(`Deactivated ${removed} scheme record${removed === 1 ? "" : "s"}`);
      if (failed.length) toast.error(`${failed.length} failed: ${failed.join("; ")}`);
      onSaved?.(); onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="simple-scheme-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {duplicating ? "Duplicate as new scheme"
              : effectiveIsEdit ? `Edit scheme ${existing?.code || ""}` : "New scheme (simple)"}
          </DialogTitle>
          <DialogDescription>
            {duplicating
              ? "Same SKUs, distributors, and sub-zones as the original — change the eligible quantity and/or reward below, then save to create it as a brand-new scheme."
              : effectiveIsEdit
              ? "Update this scheme's eligible quantity, reward, and eligible distributors."
              : "Pick SKUs with their eligible quantity, then what buying that much gives — a free item or a flat discount. Each SKU triggers on its own; behind the scenes this creates one linked scheme record per SKU."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Field label="Scheme name">
              <Input value={name} onChange={e => setName(e.target.value)}
                     placeholder="e.g. Gold CTC Leaf 24kg → Free Jeeru"
                     data-testid="simple-scheme-name"/>
            </Field>
          </div>
          <Field label="Order flow">
            <Select value={orderChannel} onValueChange={setOrderChannel}>
              <SelectTrigger data-testid="simple-scheme-channel"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="SECONDARY">Secondary (retailer orders)</SelectItem>
                <SelectItem value="PRIMARY">Primary (distributor → company)</SelectItem>
                <SelectItem value="BOTH">Both</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Valid from"><Input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} data-testid="simple-scheme-valid-from"/></Field>
          <Field label="Valid to (optional)"><Input type="date" value={validTo} onChange={e => setValidTo(e.target.value)} data-testid="simple-scheme-valid-to"/></Field>
        </div>

        <div className="border-t pt-3 space-y-2">
          <Field label="Eligible sub-zones"
                 help="Applies to every distributor in the picked sub-zone(s) — most schemes are assigned this way rather than distributor-by-distributor. Leave empty to not restrict by sub-zone.">
            <Input value={subZoneSearch} onChange={e => setSubZoneSearch(e.target.value)}
                   placeholder="Search sub-zones by code or name…"
                   className="h-8 text-xs mb-1.5" data-testid="simple-scheme-subzone-search"/>
            <div className="flex items-center gap-2 mb-1">
              <button type="button" onClick={selectAllShownSubZones}
                      className="text-[10px] text-[#6D2C91] font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-subzone-select-all">
                Select all{subZoneSearch.trim() ? ` (${shownSubZones.length})` : ""}
              </button>
              <button type="button" onClick={clearShownSubZones}
                      className="text-[10px] text-neutral-500 font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-subzone-clear-all">
                Clear{subZoneSearch.trim() ? " shown" : " all"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-[92px] overflow-y-auto p-2 border border-neutral-200 rounded-md"
                 data-testid="simple-scheme-subzones">
              {shownSubZones.length === 0 && <span className="text-[11px] text-neutral-400">No sub-zones</span>}
              {shownSubZones.map(z => {
                const active = subZoneIds.includes(z.id);
                return (
                  <button key={z.id} type="button" onClick={() => toggleSubZone(z.id)}
                          className={"text-[11px] px-2 py-1 rounded-full border transition-colors "
                            + (active ? "tulsi-gradient text-white border-transparent"
                                      : "bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100")}
                          data-testid={`simple-scheme-subzone-${z.id}`}>
                    {[z.code, z.name].filter(Boolean).join(" — ")}
                    {active && <X className="inline h-3 w-3 ml-1"/>}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="border-t pt-3 space-y-2">
          <Field label="Eligible distributors"
                 help={subZoneIds.length > 0
                   ? "Narrowed to distributors mapped to the sub-zone(s) picked above. Leave empty here to apply to all of them; pick specific ones to restrict further."
                   : "Leave empty to apply to every distributor. Pick specific ones to restrict this scheme to only them (independent of the sub-zone filter above)."}>
            <Input value={distSearch} onChange={e => setDistSearch(e.target.value)}
                   placeholder="Search distributors by code or name…"
                   className="h-8 text-xs mb-1.5" data-testid="simple-scheme-distributor-search"/>
            <div className="flex items-center gap-2 mb-1">
              <button type="button" onClick={selectAllShownDists}
                      className="text-[10px] text-[#6D2C91] font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-distributor-select-all">
                Select all{distSearch.trim() ? ` (${shownDists.length})` : ""}
              </button>
              <button type="button" onClick={clearShownDists}
                      className="text-[10px] text-neutral-500 font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-distributor-clear-all">
                Clear{distSearch.trim() ? " shown" : " all"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-[92px] overflow-y-auto p-2 border border-neutral-200 rounded-md"
                 data-testid="simple-scheme-distributors">
              {shownDists.length === 0 && (
                <span className="text-[11px] text-neutral-400">
                  {subZoneIds.length > 0 ? "No distributors mapped to the picked sub-zone(s)" : "No distributors"}
                </span>
              )}
              {shownDists.map(d => {
                const active = distributorIds.includes(d.id);
                return (
                  <button key={d.id} type="button" onClick={() => toggleDistributor(d.id)}
                          className={"text-[11px] px-2 py-1 rounded-full border transition-colors "
                            + (active ? "tulsi-gradient text-white border-transparent"
                                      : "bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100")}
                          data-testid={`simple-scheme-distributor-${d.id}`}>
                    {[d.code, d.name].filter(Boolean).join(" — ")}
                    {active && <X className="inline h-3 w-3 ml-1"/>}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-800">SKUs &amp; eligible quantity</div>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="simple-scheme-add-row">
              <Plus className="h-3 w-3 mr-1"/> Add SKU
            </Button>
          </div>
          {effectiveIsEdit && (
            <p className="text-[11px] text-neutral-500">
              {siblingsLoading ? "Loading every SKU under this scheme…" :
                "Every SKU under this scheme, loaded together — remove a row to drop just that SKU, or add more. Each still triggers independently."}
            </p>
          )}

          <Field label="Pick SKUs from a list"
                 help="Tick as many as you need, then add them all at once — each still gets its own row below to set its eligible quantity.">
            <Input value={skuPickerSearch} onChange={e => setSkuPickerSearch(e.target.value)}
                   placeholder="Search SKUs by name or code…"
                   className="h-8 text-xs mb-1.5" data-testid="simple-scheme-sku-picker-search"/>
            <div className="flex items-center gap-2 mb-1">
              <button type="button" onClick={selectAllShownSkuPicker}
                      className="text-[10px] text-[#6D2C91] font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-sku-picker-select-all">
                Select all{skuPickerSearch.trim() ? ` (${shownSkuPicker.length})` : ""}
              </button>
              <button type="button" onClick={clearShownSkuPicker}
                      className="text-[10px] text-neutral-500 font-semibold underline decoration-dotted"
                      data-testid="simple-scheme-sku-picker-clear-all">
                Clear{skuPickerSearch.trim() ? " shown" : " all"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto p-2 border border-neutral-200 rounded-md"
                 data-testid="simple-scheme-sku-picker">
              {shownSkuPicker.length === 0 && <span className="text-[11px] text-neutral-400">No SKUs</span>}
              {shownSkuPicker.map(s => {
                const already = rows.some(r => r.sku_id === s.id);
                const active = skuPickerIds.includes(s.id);
                return (
                  <button key={s.id} type="button" onClick={() => !already && toggleSkuPicker(s.id)}
                          disabled={already}
                          className={"text-[11px] px-2 py-1 rounded-full border transition-colors "
                            + (already ? "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed"
                              : active ? "tulsi-gradient text-white border-transparent"
                                       : "bg-neutral-50 text-neutral-600 border-neutral-200 hover:bg-neutral-100")}
                          data-testid={`simple-scheme-sku-picker-${s.id}`}>
                    {skuDisplayLabel(s)}{already ? " (added)" : ""}
                    {active && !already && <X className="inline h-3 w-3 ml-1"/>}
                  </button>
                );
              })}
            </div>
            <Button size="sm" onClick={addPickedSkus} disabled={skuPickerIds.length === 0}
                    className="mt-1.5 tulsi-gradient text-white font-semibold"
                    data-testid="simple-scheme-sku-picker-add">
              <Plus className="h-3 w-3 mr-1"/> Add selected{skuPickerIds.length > 0 ? ` (${skuPickerIds.length})` : ""}
            </Button>
          </Field>

          {rows.map((row, i) => {
            const sku = skuById(row.sku_id);
            return (
              <div key={i} className="grid grid-cols-[1fr_100px_90px_auto] gap-2 items-end">
                <Field label={i === 0 ? "SKU" : undefined}>
                  <SearchableSelect
                    value={row.sku_id} onChange={v => { patchRow(i, "sku_id", v); patchRow(i, "unit", "KG"); }}
                    options={skus.map(s => ({ id: s.id, label: skuDisplayLabel(s) }))}
                    placeholder="Select…" testid={`simple-scheme-sku-${i}`}
                  />
                </Field>
                <Field label={i === 0 ? "Eligible qty" : undefined}>
                  <Input type="number" value={row.qty} onChange={e => patchRow(i, "qty", e.target.value)}
                         data-testid={`simple-scheme-qty-${i}`}/>
                </Field>
                <Field label={i === 0 ? "Unit" : undefined}>
                  <Select value={row.unit} onValueChange={v => patchRow(i, "unit", v)}>
                    <SelectTrigger data-testid={`simple-scheme-unit-${i}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {unitOptionsFor(sku).map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Button size="sm" variant="ghost" onClick={() => removeRow(i)}
                        disabled={rows.length === 1} data-testid={`simple-scheme-row-del-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-500"/>
                </Button>
              </div>
            );
          })}
        </div>

        <div className="border-t pt-3 space-y-3">
          <div className="text-sm font-semibold text-neutral-800">Given scheme</div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRewardType("FREE_ITEM")}
                    data-testid="simple-scheme-reward-free"
                    className={"px-3 h-9 rounded-lg text-xs font-semibold border "
                      + (rewardType === "FREE_ITEM" ? "bg-[#6D2C91] text-white border-[#6D2C91]" : "bg-white text-neutral-600 border-neutral-200")}>
              Free item
            </button>
            <button type="button" onClick={() => setRewardType("FREE_SKU")}
                    data-testid="simple-scheme-reward-free-sku"
                    className={"px-3 h-9 rounded-lg text-xs font-semibold border "
                      + (rewardType === "FREE_SKU" ? "bg-[#6D2C91] text-white border-[#6D2C91]" : "bg-white text-neutral-600 border-neutral-200")}>
              Free SKU (stocked)
            </button>
            <button type="button" onClick={() => setRewardType("DISCOUNT")}
                    data-testid="simple-scheme-reward-discount"
                    className={"px-3 h-9 rounded-lg text-xs font-semibold border "
                      + (rewardType === "DISCOUNT" ? "bg-[#6D2C91] text-white border-[#6D2C91]" : "bg-white text-neutral-600 border-neutral-200")}>
              Discount (₹ off)
            </button>
          </div>

          {rewardType === "FREE_ITEM" ? (
            <Field label="Free item"
                   help="Plain description, not tied to stock — e.g. 1 Kg Jeera, 1 Bottle, 1 Coupon. Shown on the order line; nothing is deducted from inventory for it.">
              <Input value={freeItemText} onChange={e => setFreeItemText(e.target.value)}
                     placeholder="e.g. 1 Kg Jeera" data-testid="simple-scheme-free-text"/>
            </Field>
          ) : rewardType === "FREE_SKU" ? (
            <div className="grid grid-cols-[1fr_100px_90px] gap-2 items-end">
              <Field label="Free item SKU"
                     help="Any SKU works, including a non-orderable gift article (see the SKU form Orderable checkbox) for things like 1 Container or 1 Cup Saucer that are not normally sold. A real order line is created and deducted from this SKU's stock.">
                <SearchableSelect
                  value={freeSkuId} onChange={v => { setFreeSkuId(v); setFreeUnit("KG"); }}
                  options={skus.map(s => ({ id: s.id, label: skuDisplayLabel(s) }))}
                  placeholder="Select…" testid="simple-scheme-free-sku"
                />
              </Field>
              <Field label="Qty">
                <Input type="number" value={freeQty} onChange={e => setFreeQty(e.target.value)} data-testid="simple-scheme-free-qty"/>
              </Field>
              <Field label="Unit">
                <Select value={freeUnit} onValueChange={setFreeUnit}>
                  <SelectTrigger data-testid="simple-scheme-free-unit"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {unitOptionsFor(skuById(freeSkuId)).map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          ) : (
            <Field label="Discount amount (₹)" help="Deducted from that line's value once the eligible quantity is reached (capped at the line's own total).">
              <Input type="number" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} data-testid="simple-scheme-discount-amount"/>
            </Field>
          )}
        </div>

        <DialogFooter>
          {effectiveIsEdit && (
            <>
              <Button variant="outline" onClick={deactivateWholeScheme} disabled={busy || siblingsLoading}
                      className="mr-auto text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="simple-scheme-deactivate-all">
                Deactivate whole scheme
              </Button>
              <Button variant="outline" onClick={duplicateAsNew} disabled={busy || siblingsLoading}
                      data-testid="simple-scheme-duplicate">
                Duplicate as new scheme
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || siblingsLoading}
                  className="tulsi-gradient text-white font-semibold"
                  data-testid="simple-scheme-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : null}
            {effectiveIsEdit ? "Save changes" : duplicating ? "Create duplicate" : "Create scheme"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Routes a scheme's Edit button back to whichever form created it — a
// scheme built with the simple wizard (ui_source: "simple") reopens that
// same guided single-SKU editor; anything else (including schemes built
// with the full form, or older schemes with no ui_source at all) opens the
// full raw scheme-type/conditions editor.
// Schemes created before `ui_source` existed have no tag at all — recognize
// the simple wizard's shape anyway (single buy-SKU FREE_GOODS trigger, no
// zone/outlet/category filters) so they still route to the guided editor
// instead of falling back to the raw form just for lacking a field that
// didn't exist yet when they were made.
export function looksLikeSimpleScheme(existing) {
  if (!existing || existing.scheme_type !== "FREE_GOODS") return false;
  const app = existing.applicability || {};
  if ((app.zone_ids || []).length || (app.outlet_type_lookup_ids || []).length
      || (app.product_category_lookup_ids || []).length) return false;
  const c = existing.conditions || {};
  return !!(c.buy_sku_id && c.buy_qty);
}

export function SchemeEditForm({ open, onOpenChange, onSaved, existing }) {
  return (existing?.ui_source === "simple" || looksLikeSimpleScheme(existing))
    ? <SimpleSchemeForm open={open} onOpenChange={onOpenChange} onSaved={onSaved} existing={existing}/>
    : <SchemeForm open={open} onOpenChange={onOpenChange} onSaved={onSaved} existing={existing}/>;
}
